import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Medal } from "lucide-react";
import { Card, CardHeader, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { useAuth } from "contexts/AuthContext";
import { useI18n } from "i18n";
import api from "lib/api";

export default function SportForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const isEditing = !!id;

  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);
  const [error, setError] = useState("");

  const canManage = !!user?.capabilities?.manage_events;

  useEffect(() => {
    if (!isEditing) return;
    api
      .get(`/api/sports/${id}/`)
      .then((res) => setName(res.data.name || ""))
      .catch(() => setError(t("common.loadError")))
      .finally(() => setFetching(false));
  }, [id, isEditing, t]);

  if (user && !canManage) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isEditing) {
        await api.put(`/api/sports/${id}/`, { name });
      } else {
        await api.post("/api/sports/", { name });
      }
      navigate("/app/sports");
    } catch (err) {
      const detail = err.response?.data;
      setError(
        typeof detail === "object"
          ? Object.values(detail).flat().join(" ")
          : t("common.saveError")
      );
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="w-full animate-fade-in">
      <button
        onClick={() => navigate("/app/sports")}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("common.back")}
      </button>

      <Card className="w-full">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Medal className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight">
                {isEditing ? t("sport.edit") : t("sport.new")}
              </h1>
              <p className="text-sm text-muted-foreground">
                {isEditing ? t("sport.editSubtitle") : t("sport.newSubtitle")}
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="w-full space-y-6">
            {error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div>
              <Label htmlFor="name">{t("sport.name")}</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={120}
                placeholder={t("sport.namePlaceholder")}
              />
            </div>

            <div className="flex justify-end gap-3 border-t border-border pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/app/sports")}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" loading={loading}>
                {isEditing ? t("common.save") : t("common.create")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
